import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  invertedActionButtonStyles,
  semanticTone,
} from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import { AgentEditContext } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import AttachKnowledgeDialog from 'components/@home/@settings/Tabs/Agents/AttachKnowledgeDialog';
import AttachToolsDialog from 'components/@home/@settings/Tabs/Agents/AttachToolsDialog';
import IdentityCard from 'components/@home/@settings/Tabs/Agents/IdentityCard';
import InstructionEditor from 'components/@home/@settings/Tabs/Agents/InstructionEditor';
import InstructionSaveFooter from 'components/@home/@settings/Tabs/Agents/InstructionSaveFooter';
import {
  attachTool as attachToolHelper,
  attachedToolIdSet,
  sanitizeAttachmentsForApi,
  setAttachmentUsage,
} from 'components/@home/@settings/Tabs/Agents/attachedToolsHelpers';
import {
  attachedKnowledgeIdSet,
  sanitizeKnowledgeAttachmentsForApi,
  setKnowledgeAttachmentUsage,
} from 'components/@home/@settings/Tabs/Agents/attachedKnowledgeHelpers';
import { ChatFlowContext } from 'components/@home/@settings/Tabs/ChatFlowContext';
import ChatFlowContextHeader from 'components/@home/@settings/Tabs/ChatFlowContextHeader';
import connector from 'components/@home/@settings/Tabs/connector';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import CircleSmallIcon from 'mdi-react/CircleSmallIcon';
import CogOutlineIcon from 'mdi-react/CogOutlineIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import FileDocumentOutlineIcon from 'mdi-react/FileDocumentOutlineIcon';
import FileTreeOutlineIcon from 'mdi-react/FileTreeOutlineIcon';
import HomeOutlineIcon from 'mdi-react/HomeOutlineIcon';
import MinusIcon from 'mdi-react/MinusIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import TrayArrowDownIcon from 'mdi-react/TrayArrowDownIcon';
import TrayArrowUpIcon from 'mdi-react/TrayArrowUpIcon';
import DatabaseOutlineIcon from 'mdi-react/DatabaseOutlineIcon';
import WrenchOutlineIcon from 'mdi-react/WrenchOutlineIcon';
import { bool, func, object, string } from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import Agent from 'services/api/Agent';
import AgentInstruction from 'services/api/AgentInstruction';
import AgentSkill from 'services/api/AgentSkill';
import AgentTool from 'services/api/AgentTool';
import { buildAgenticPath } from 'utils/agenticRoute';
import agenticSectionPath from 'utils/agenticSectionPath';
import agenticSlug from 'utils/agenticSlug';
import { I18n } from 'utils/i18n';
import toolNameConflictMessage from 'utils/toolNameConflict';

const getStyles = theme => {
  const mutedChip = semanticTone(theme, 'default');
  return {
    root: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    scroll: {
      flex: 1,
      overflowY: 'auto',
    },
    pageInner: {
      margin: '0 auto',
      padding: theme.spacing(3, 4),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2.5),
    },
    attachedPill: {
      height: 22,
      fontSize: theme.typography.pxToRem(13),
      fontWeight: 500,
      borderRadius: theme.shape.borderRadius,
      borderColor: theme.palette.divider,
      '& .MuiChip-icon': {
        color: theme.palette.warning.main,
        width: 40,
        height: 40,
        marginRight: '-16px',
        marginLeft: '-8px',
      },
    },
    attachedPillTooltip: {
      padding: 0,
    },
    identityPopoverFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(0.75),
      paddingTop: theme.spacing(0.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.text.secondary,
    },
    attachedDot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: theme.palette.warning.main,
      flexShrink: 0,
    },
    tabsRoot: {
      minHeight: 44,
      borderBottom: `1px solid ${theme.palette.divider}`,
      '& .MuiTabs-flexContainer': { gap: theme.spacing(2.5) },
    },
    tabRoot: {
      textTransform: 'none',
      minHeight: 44,
      minWidth: 'auto',
      padding: theme.spacing(1.25, 0.25),
      fontSize: theme.typography.pxToRem(13),
      fontWeight: 500,
      color: theme.palette.text.secondary,
      '&.Mui-selected': {
        color: theme.palette.text.primary,
      },
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
    },
    sectionTitle: {
      fontSize: theme.typography.pxToRem(18),
      fontWeight: 700,
      color: theme.palette.text.primary,
      letterSpacing: '-0.3px',
    },
    sectionHelper: {
      fontSize: theme.typography.pxToRem(13),
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    sectionActions: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    secondaryButton: {
      textTransform: 'none',
      fontWeight: 500,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      borderRadius: '6px',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        border: `1px solid ${theme.palette.divider}`,
      },
    },
    toolList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    toolCard: {
      display: 'flex',
      flexDirection: 'column',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '10px',
      backgroundColor: theme.palette.background.paper,
      overflow: 'hidden',
    },
    toolCardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 2),
      cursor: 'pointer',
      transition: 'background-color 120ms ease',
      '&:hover': { backgroundColor: theme.palette.action.hover },
    },
    toolCardUsage: {
      padding: theme.spacing(1.25, 2, 1.5),
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
    toolCardUsageHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
    },
    toolCardUsageLabel: {
      fontSize: theme.typography.pxToRem(11.5),
      fontWeight: 700,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
    },
    toolCardUsageHint: {
      fontSize: theme.typography.pxToRem(11.5),
      color: theme.palette.text.secondary,
    },
    toolCardUsageInput: {
      backgroundColor: theme.palette.background.paper,
      '& .MuiOutlinedInput-root': {
        backgroundColor: theme.palette.background.paper,
      },
    },
    toolCardUsageLink: {
      alignSelf: 'flex-start',
      marginTop: theme.spacing(0.5),
      padding: 0,
      minWidth: 0,
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 600,
      color: theme.palette.primary.main,
      textTransform: 'none',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
        backgroundColor: 'transparent',
      },
    },
    toolIcon: {
      width: 36,
      height: 36,
      borderRadius: '8px',
      backgroundColor: theme.palette.action.subtle,
      color: theme.palette.text.secondary,
    },
    toolText: { flex: 1, minWidth: 0 },
    toolNameRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      minWidth: 0,
    },
    toolName: {
      fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      fontSize: theme.typography.pxToRem(13),
      color: theme.palette.text.primary,
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    kindBadge: {
      height: 20,
      fontSize: theme.typography.pxToRem(10.5),
      fontWeight: 600,
      letterSpacing: '0.2px',
      backgroundColor: mutedChip.bg,
      color: mutedChip.fg,
      borderRadius: '4px',
      '& .MuiChip-label': { padding: '0 6px' },
    },
    toolDesc: {
      fontSize: theme.typography.pxToRem(12.5),
      color: theme.palette.text.secondary,
      marginTop: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    emptyCard: {
      border: `1px dashed ${theme.palette.divider}`,
      borderRadius: '10px',
      padding: theme.spacing(3),
      textAlign: 'center',
      color: theme.palette.text.secondary,
      fontSize: theme.typography.pxToRem(13),
    },
    manageToolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
    },
    manageSearch: {
      flex: 1,
      '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        backgroundColor: theme.palette.background.paper,
      },
    },
    manageList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      maxHeight: 420,
      overflowY: 'auto',
    },
    manageToolRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.25, 1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '10px',
      backgroundColor: theme.palette.background.paper,
    },
    attachButton: {
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: '8px',
      minWidth: 84,
      ...invertedActionButtonStyles(theme),
    },
    attachedLabel: {
      textTransform: 'none',
      fontWeight: 600,
      borderRadius: '8px',
      minWidth: 84,
      color: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
      pointerEvents: 'none',
    },
    moreActionsButton: {
      borderColor: theme.palette.divider,
      minWidth: 0,
    },
  };
};

const AttachedToolUsageEditor = ({
  styles,
  isSendMessage = false,
  value = '',
  onSave,
  autoFocus = false,
  onCollapse = null,
}) => {
  const [draft, setDraft] = useState(value || '');
  const lastSavedRef = useRef(value || '');

  useEffect(() => {
    setDraft(value || '');
    lastSavedRef.current = value || '';
  }, [value]);

  const commit = () => {
    const next = draft;
    if (next === lastSavedRef.current) return;
    lastSavedRef.current = next;
    onSave(next);
  };

  return (
    <Box sx={styles.toolCardUsage} onClick={e => e.stopPropagation()}>
      <Box sx={styles.toolCardUsageHeader}>
        <Typography sx={styles.toolCardUsageLabel}>
          {I18n.t('AgenticChatFlow.UsageLabel')}
        </Typography>
        {onCollapse && (
          <Button
            size="small"
            variant="text"
            color="inherit"
            sx={styles.toolCardUsageLink}
            onClick={() => {
              commit();
              onCollapse();
            }}
          >
            {I18n.t('AgenticChatFlow.HideUsage')}
          </Button>
        )}
      </Box>
      <TextField
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        size="small"
        fullWidth
        multiline
        minRows={2}
        autoFocus={autoFocus}
        sx={styles.toolCardUsageInput}
        placeholder={
          isSendMessage
            ? I18n.t('AgenticChatFlow.UsagePlaceholderSendMessage')
            : I18n.t('AgenticChatFlow.UsagePlaceholder')
        }
      />
      <Typography sx={styles.toolCardUsageHint}>
        {isSendMessage
          ? I18n.t('AgenticChatFlow.UsageHintSendMessage')
          : I18n.t('AgenticChatFlow.UsageHint')}
      </Typography>
    </Box>
  );
};

AttachedToolUsageEditor.propTypes = {
  styles: object.isRequired,
  isSendMessage: bool,
  value: string,
  onSave: func.isRequired,
  autoFocus: bool,
  onCollapse: func,
};

const SkillEditor = ({ skillId: skillIdProp = null, authCompany = null, embedded = false }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams();
  const id = skillIdProp || routeId;
  const {
    context,
    selectedFlowId,
    chatFlows: flowsMap,
    onBackToList,
  } = useContext(ChatFlowContext);
  const parentFlow = selectedFlowId ? flowsMap?.[selectedFlowId] : null;
  const agentEdit = useContext(AgentEditContext);
  const importInputRef = useRef(null);
  const [skill, setSkill] = useState(null);
  const [tools, setTools] = useState([]);
  const [agents, setAgents] = useState([]);
  const [tab, setTab] = useState('instructions');

  useEffect(() => {
    const nextTab = location.state?.skillTab;
    if (nextTab === 'tools' || nextTab === 'knowledge' || nextTab === 'instructions') {
      setTab(nextTab);
    }
  }, [id, location.state?.skillTab]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [identityAnchor, setIdentityAnchor] = useState(null);
  const [draftDisplayName, setDraftDisplayName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageKnowledgeOpen, setManageKnowledgeOpen] = useState(false);
  const [newToolMenuAnchor, setNewToolMenuAnchor] = useState(null);
  const [createToolOpen, setCreateToolOpen] = useState(false);
  const [newToolForm, setNewToolForm] = useState({ name: '', kind: 'http' });
  const [creatingTool, setCreatingTool] = useState(false);
  const [createToolError, setCreateToolError] = useState('');
  const [createNavigateAfter, setCreateNavigateAfter] = useState(false);
  const [instructionReloadKey, setInstructionReloadKey] = useState(0);
  const [instructionDirty, setInstructionDirty] = useState(false);
  const [instructionSaveFeedback, setInstructionSaveFeedback] = useState(null);
  const instructionEditorRef = useRef(null);
  const instructionSaveFeedbackTimerRef = useRef(null);
  const [usageOpen, setUsageOpen] = useState(() => new Set());
  const usageInitRef = useRef(false);

  const clearInstructionSaveFeedbackLater = useCallback(() => {
    if (instructionSaveFeedbackTimerRef.current) {
      clearTimeout(instructionSaveFeedbackTimerRef.current);
    }
    instructionSaveFeedbackTimerRef.current = setTimeout(() => {
      setInstructionSaveFeedback(null);
    }, 2400);
  }, []);

  useEffect(
    () => () => {
      if (instructionSaveFeedbackTimerRef.current) {
        clearTimeout(instructionSaveFeedbackTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (instructionDirty && instructionSaveFeedback?.severity === 'success') {
      setInstructionSaveFeedback(null);
    }
  }, [instructionDirty, instructionSaveFeedback?.severity]);

  const handleInstructionFooterSave = useCallback(() => {
    instructionEditorRef.current?.openSaveDialog?.();
  }, []);

  useEffect(() => {
    if (!id) return;
    Promise.all([AgentSkill.get(id), AgentTool.list(), Agent.list()]).then(([s, t, a]) => {
      setSkill(s);
      setTools(t || []);
      setAgents(a || []);
    });
  }, [id]);

  const attachedAgentsOther = useMemo(() => {
    if (!skill) return [];
    const sid = String(skill._id);
    return agents.filter(a => {
      const ids = (a.skillIds || a.draft?.skillIds || []).map(String);
      return a._id !== agentEdit?.agent?._id && ids.includes(sid);
    });
  }, [agents, skill, agentEdit?.agent?._id]);

  const attachedAgentCountOther = attachedAgentsOther.length;

  const isAttachedToCurrentAgent = useMemo(() => {
    if (!agentEdit?.agent || !skill) return false;
    return (agentEdit.agent.skillIds || []).map(String).includes(String(skill._id));
  }, [agentEdit?.agent, skill]);

  const canDetach = Boolean(embedded && isAttachedToCurrentAgent && agentEdit?.onSaveAgent);

  const skillAttachments = useMemo(() => skill?.attachedTools || [], [skill]);
  const skillKnowledgeAttachments = useMemo(() => skill?.attachedKnowledge || [], [skill]);
  const catalogKnowledge = useMemo(() => agentEdit?.knowledge || [], [agentEdit?.knowledge]);
  const attachedKnowledgeIds = useMemo(
    () => attachedKnowledgeIdSet(skillKnowledgeAttachments),
    [skillKnowledgeAttachments],
  );
  const attachedKnowledge = useMemo(
    () => catalogKnowledge.filter(k => attachedKnowledgeIds.has(String(k._id))),
    [catalogKnowledge, attachedKnowledgeIds],
  );
  const attachedToolIds = useMemo(() => attachedToolIdSet(skillAttachments), [skillAttachments]);
  const attachedTools = useMemo(
    () => tools.filter(t => attachedToolIds.has(String(t._id))),
    [tools, attachedToolIds],
  );
  const attachedBuiltins = useMemo(
    () => skillAttachments.filter(a => a.builtin),
    [skillAttachments],
  );

  useEffect(() => {
    if (!skill || usageInitRef.current) return;
    usageInitRef.current = true;
    const initial = new Set(
      [...skillAttachments, ...skillKnowledgeAttachments]
        .filter(att => (att.usage || '').trim())
        .map(att =>
          att.tool
            ? `tool:${String(att.tool)}`
            : att.builtin
              ? `builtin:${att.builtin}`
              : att.knowledge
                ? `knowledge:${String(att.knowledge)}`
                : '',
        )
        .filter(Boolean),
    );
    if (initial.size > 0) setUsageOpen(initial);
  }, [skill, skillAttachments, skillKnowledgeAttachments]);

  const toggleUsageOpen = (key, open) => {
    setUsageOpen(prev => {
      const next = new Set(prev);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const contextBreadcrumbs = useMemo(() => {
    if (!skill) return null;
    const items = [];
    items.push({
      key: 'home',
      icon: <HomeOutlineIcon size={14} />,
      onClick: onBackToList || context?.clear,
      title: I18n.t('AgenticChatFlow.AgentsTitle'),
      ariaLabel: I18n.t('AgenticChatFlow.AgentsTitle'),
    });
    if (parentFlow) {
      items.push({
        key: 'agent',
        label: parentFlow.name,
        onClick: context?.clear,
      });
      items.push({
        key: 'skills',
        label: I18n.t('AgenticChatFlow.NavSkills'),
        onClick: context?.pop,
      });
    } else if (context?.pop) {
      items.push({
        key: 'skills',
        label: I18n.t('AgenticChatFlow.Skills'),
        onClick: context.pop,
      });
    }
    items.push({
      key: 'current',
      label: skill.displayName || skill.name,
    });
    return items;
  }, [skill, context, parentFlow, onBackToList]);

  const saveIdentity = async () => {
    setSavingIdentity(true);
    try {
      const updated = await AgentSkill.update(id, {
        displayName: draftDisplayName,
        description: draftDescription,
      });
      setSkill(updated);
      setIdentityAnchor(null);
    } finally {
      setSavingIdentity(false);
    }
  };

  const saveAttachments = async attachments => {
    const updated = await AgentSkill.update(id, {
      displayName: skill.displayName,
      description: skill.description,
      attachedTools: sanitizeAttachmentsForApi(attachments),
      attachedKnowledge: sanitizeKnowledgeAttachmentsForApi(skillKnowledgeAttachments),
    });
    setSkill(updated);
  };

  const saveKnowledgeAttachments = async attachments => {
    const updated = await AgentSkill.update(id, {
      displayName: skill.displayName,
      description: skill.description,
      attachedTools: sanitizeAttachmentsForApi(skillAttachments),
      attachedKnowledge: sanitizeKnowledgeAttachmentsForApi(attachments),
    });
    setSkill(updated);
  };

  const saveAttachmentUsage = async ({ tool: toolId, builtin }, usage) => {
    const next = setAttachmentUsage(skillAttachments, { tool: toolId, builtin }, usage);
    await saveAttachments(next);
  };

  const saveKnowledgeAttachmentUsage = async (knowledgeId, usage) => {
    const next = setKnowledgeAttachmentUsage(skillKnowledgeAttachments, knowledgeId, usage);
    await saveKnowledgeAttachments(next);
  };

  const handleDelete = async () => {
    setMenuAnchor(null);
    await AgentSkill.destroy(id);
    if (context?.pop) context.pop();
    else navigate(agenticSectionPath(authCompany));
  };

  const handleDetach = async () => {
    setMenuAnchor(null);
    if (!canDetach) return;
    const sid = String(skill._id);
    const skillIds = (agentEdit.agent.skillIds || []).map(String).filter(x => x !== sid);
    await agentEdit.onSaveAgent({ skillIds });
    if (agentEdit.flow?._id && agentEdit.authCompany) {
      navigate(
        buildAgenticPath(agentEdit.authCompany, {
          flowId: agentEdit.flow._id,
          section: 'skills',
        }),
      );
    } else if (context?.pop) {
      context.pop();
    }
  };

  const exportSkill = async () => {
    setMenuAnchor(null);
    const instructions = await AgentInstruction.list('skill', id);
    const active = instructions.find(item => item.active) || instructions[instructions.length - 1];
    const markdown = active?.blocks?.[0]?.text || '';
    const payload = {
      version: 1,
      skill: {
        name: skill.name,
        displayName: skill.displayName,
        description: skill.description,
        tools: attachedTools.map(tool => tool.name),
      },
      instructions: { markdown },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    const base =
      (skill.displayName || skill.name || 'skill')
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '') || 'skill';
    link.download = `${base}.skill.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const openImportSkill = () => {
    setMenuAnchor(null);
    importInputRef.current?.click();
  };

  const handleImportSkillFile = async event => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (input) input.value = '';
    if (!file) return;

    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const imported = payload?.skill || payload;
      const toolNames = Array.isArray(imported.tools) ? imported.tools : [];
      const importedToolIds = toolNames
        .map(name => tools.find(t => t.name === name)?._id)
        .filter(Boolean);
      const importedAttachments = importedToolIds.length
        ? importedToolIds.map(tid => ({ tool: tid, usage: '' }))
        : skill.attachedTools || [];
      const updated = await AgentSkill.update(id, {
        displayName: imported.displayName ?? skill.displayName,
        description: imported.description ?? skill.description,
        attachedTools: sanitizeAttachmentsForApi(importedAttachments),
      });
      setSkill(updated);

      const markdown = payload?.instructions?.markdown;
      if (typeof markdown === 'string' && markdown.trim()) {
        const created = await AgentInstruction.create({
          entityType: 'skill',
          entityId: id,
          blocks: [{ type: 'textarea', text: markdown }],
          commit: I18n.t('AgenticChatFlow.Import'),
        });
        if (created?._id) {
          await AgentInstruction.activate(created._id);
        }
        setInstructionReloadKey(key => key + 1);
      }
    } catch {
      // ignore invalid import files
    }
  };

  if (!skill) return null;

  const handleIdentityOpen = event => {
    setDraftDisplayName(skill.displayName || '');
    setDraftDescription(skill.description || '');
    setIdentityAnchor(event.currentTarget);
  };

  const closeIdentityPopover = () => {
    if (!savingIdentity) setIdentityAnchor(null);
  };

  const popoverAttachedLabel = attachedAgentsOther.map(a => a.name).join(', ');

  const openManage = () => {
    setManageOpen(true);
  };

  const openManageKnowledge = () => {
    setManageKnowledgeOpen(true);
  };

  const openNewToolMenu = (event, { navigateAfter = false } = {}) => {
    setCreateNavigateAfter(navigateAfter);
    setNewToolMenuAnchor(event.currentTarget);
  };

  const startCreateTool = kind => {
    setNewToolMenuAnchor(null);
    setNewToolForm({ name: '', kind });
    setCreateToolError('');
    setCreateToolOpen(true);
  };

  const handleCreateTool = async () => {
    const name = agenticSlug(newToolForm.name);
    setCreatingTool(true);
    setCreateToolError('');
    try {
      const tool = await AgentTool.create({
        kind: newToolForm.kind,
        name,
        http:
          newToolForm.kind === 'http'
            ? { method: 'POST', urlTemplate: 'https://example.com/webhook', outputMapping: {} }
            : undefined,
        codeKey: newToolForm.kind === 'code' ? name : undefined,
      });
      setTools(prev => [...prev, tool].sort((a, b) => a.name.localeCompare(b.name)));
      setCreateToolOpen(false);
      const nextAttachments = attachToolHelper(skillAttachments, tool._id);
      await saveAttachments(nextAttachments);
      if (createNavigateAfter && context?.push) {
        context.push({ type: 'agent-tool', agentTool: tool._id });
      }
    } catch (err) {
      setCreateToolError(toolNameConflictMessage(err));
    } finally {
      setCreatingTool(false);
    }
  };

  const totalAttached = attachedTools.length + attachedBuiltins.length;
  const toolsTabLabel =
    totalAttached > 0
      ? `${I18n.t('AgenticChatFlow.Tools')} (${totalAttached})`
      : I18n.t('AgenticChatFlow.Tools');

  const totalKnowledgeAttached = attachedKnowledge.length;
  const knowledgeTabLabel =
    totalKnowledgeAttached > 0
      ? `${I18n.t('AgenticChatFlow.Knowledge')} (${totalKnowledgeAttached})`
      : I18n.t('AgenticChatFlow.Knowledge');

  return (
    <Box sx={[styles.root, embedded && { height: '100%', borderLeft: 'none' }]}>
      {!embedded && contextBreadcrumbs && (
        <ChatFlowContextHeader breadcrumbs={contextBreadcrumbs} />
      )}

      <Box sx={styles.scroll}>
        <Box sx={styles.pageInner}>
          <IdentityCard
            icon={<FileTreeOutlineIcon size={18} />}
            title={skill.displayName || skill.name}
            description={skill.description}
            pill={
              attachedAgentCountOther > 0 && (
                <Tooltip
                  sx={styles.attachedPillTooltip}
                  title={popoverAttachedLabel}
                  placement="top"
                  arrow
                >
                  <Chip
                    variant="outlined"
                    color="secondary"
                    size="small"
                    sx={styles.attachedPill}
                    icon={<CircleSmallIcon />}
                    label={I18n.t('AgenticChatFlow.AttachedToAgentsOther', {
                      count: attachedAgentCountOther,
                    })}
                  />
                </Tooltip>
              )
            }
            edit={{
              open: Boolean(identityAnchor),
              anchorEl: identityAnchor,
              onOpen: handleIdentityOpen,
              onClose: closeIdentityPopover,
              content: (
                <>
                  <TextField
                    value={draftDisplayName}
                    onChange={e => setDraftDisplayName(e.target.value)}
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder={I18n.t('AgenticChatFlow.DisplayName')}
                  />
                  <TextField
                    value={draftDescription}
                    onChange={e => setDraftDescription(e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder={I18n.t('AgenticChatFlow.Description')}
                    helperText={I18n.t('AgenticChatFlow.SkillDescriptionHelper')}
                  />
                  {popoverAttachedLabel && (
                    <Box sx={styles.identityPopoverFooter}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {I18n.t('AgenticChatFlow.AttachedTo')}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{
                          color: 'text.primary',
                        }}
                      >
                        {popoverAttachedLabel}
                      </Typography>
                    </Box>
                  )}
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      onClick={closeIdentityPopover}
                      disabled={savingIdentity}
                      color="inherit"
                    >
                      {I18n.t('AgenticChatFlow.Cancel')}
                    </Button>
                    <Button
                      onClick={saveIdentity}
                      variant="contained"
                      color="primary"
                      disableElevation
                      disabled={savingIdentity}
                    >
                      {I18n.t('AgenticChatFlow.Save')}
                    </Button>
                  </Stack>
                </>
              ),
            }}
            actions={
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={e => setMenuAnchor(e.currentTarget)}
                  size="small"
                  sx={styles.moreActionsButton}
                >
                  <DotsHorizontalIcon size={20} />
                </Button>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={openImportSkill}>
                    <TrayArrowUpIcon size={16} style={{ marginRight: 8 }} />
                    {I18n.t('AgenticChatFlow.ImportSkill')}
                  </MenuItem>
                  <MenuItem onClick={exportSkill}>
                    <TrayArrowDownIcon size={16} style={{ marginRight: 8 }} />
                    {I18n.t('AgenticChatFlow.ExportSkill')}
                  </MenuItem>
                  <Divider />
                  {canDetach && (
                    <MenuItem onClick={handleDetach}>
                      <MinusIcon size={16} style={{ marginRight: 8 }} />
                      {I18n.t('AgenticChatFlow.Detach')}
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <TrashCanOutlineIcon size={16} style={{ marginRight: 8 }} />
                    {I18n.t('AgenticChatFlow.Delete')}
                  </MenuItem>
                </Menu>
                <input
                  ref={importInputRef}
                  type="file"
                  accept="application/json,.json"
                  hidden
                  onChange={handleImportSkillFile}
                />
              </>
            }
          />

          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={styles.tabsRoot}
            TabIndicatorProps={{ sx: { height: 2 } }}
          >
            <Tab
              sx={styles.tabRoot}
              value="instructions"
              icon={<FileDocumentOutlineIcon size={16} />}
              iconPosition="start"
              label={I18n.t('AgenticChatFlow.TabInstructions')}
            />
            <Tab
              sx={styles.tabRoot}
              value="tools"
              icon={<WrenchOutlineIcon size={16} />}
              iconPosition="start"
              label={toolsTabLabel}
            />
            <Tab
              sx={styles.tabRoot}
              value="knowledge"
              icon={<DatabaseOutlineIcon size={16} />}
              iconPosition="start"
              label={knowledgeTabLabel}
            />
          </Tabs>

          {tab === 'instructions' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: embedded ? 1 : undefined,
                minHeight: embedded ? 0 : undefined,
                gap: 0,
              }}
            >
              <InstructionEditor
                key={`${skill._id}-${instructionReloadKey}`}
                ref={instructionEditorRef}
                entityType="skill"
                entityId={skill._id}
                title={I18n.t('AgenticChatFlow.SkillInstructionTitle')}
                availableTools={attachedTools}
                parentManagedSave
                onDirtyChange={setInstructionDirty}
                onSaved={() => {
                  setInstructionSaveFeedback({
                    severity: 'success',
                    message: I18n.t('AgenticChatFlow.ToolSaved'),
                  });
                  clearInstructionSaveFeedbackLater();
                }}
              />
            </Box>
          )}

          {tab === 'tools' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={styles.sectionHeader}>
                <Box>
                  <Typography sx={styles.sectionTitle}>
                    {I18n.t('AgenticChatFlow.Tools')}
                  </Typography>
                  <Typography sx={styles.sectionHelper}>
                    {I18n.t('AgenticChatFlow.ToolsTabHelper')}
                  </Typography>
                </Box>
                <Stack direction="row" sx={styles.sectionActions}>
                  <Button
                    sx={styles.secondaryButton}
                    startIcon={<CogOutlineIcon size={16} />}
                    onClick={openManage}
                  >
                    {I18n.t('AgenticChatFlow.ManageTools')}
                  </Button>
                  <Button
                    sx={styles.secondaryButton}
                    onClick={e => openNewToolMenu(e, { navigateAfter: true })}
                  >
                    {I18n.t('AgenticChatFlow.CreateTool')}
                  </Button>
                </Stack>
              </Box>
              {totalAttached === 0 ? (
                <Box sx={styles.emptyCard}>
                  {I18n.t('AgenticChatFlow.NoToolsConfiguredForSkill')}
                </Box>
              ) : (
                <Box sx={styles.toolList}>
                  {attachedBuiltins.map(att => {
                    const key = `builtin:${att.builtin}`;
                    const isOpen = usageOpen.has(key);
                    const hasUsage = Boolean((att.usage || '').trim());
                    return (
                      <Box key={key} sx={styles.toolCard}>
                        <Box sx={styles.toolCardHeader} onClick={openManage}>
                          <Avatar variant="rounded" sx={styles.toolIcon}>
                            <WrenchOutlineIcon size={18} />
                          </Avatar>
                          <Box sx={styles.toolText}>
                            <Box sx={styles.toolNameRow}>
                              <Typography sx={styles.toolName}>{att.builtin}</Typography>
                              <Chip
                                size="small"
                                sx={styles.kindBadge}
                                label={I18n.t('AgenticChatFlow.BuiltinTool')}
                              />
                            </Box>
                            {!isOpen && (
                              <Button
                                size="small"
                                variant="text"
                                color="inherit"
                                sx={styles.toolCardUsageLink}
                                onClick={e => {
                                  e.stopPropagation();
                                  toggleUsageOpen(key, true);
                                }}
                              >
                                {hasUsage
                                  ? I18n.t('AgenticChatFlow.EditUsage')
                                  : I18n.t('AgenticChatFlow.AddUsage')}
                              </Button>
                            )}
                          </Box>
                          <ChevronRightIcon size={20} />
                        </Box>
                        {isOpen && (
                          <AttachedToolUsageEditor
                            styles={styles}
                            isSendMessage={att.builtin === 'send_message'}
                            value={att.usage || ''}
                            autoFocus={!hasUsage}
                            onSave={usage => saveAttachmentUsage({ builtin: att.builtin }, usage)}
                            onCollapse={() => toggleUsageOpen(key, false)}
                          />
                        )}
                      </Box>
                    );
                  })}
                  {attachedTools.map(tool => {
                    const att = skillAttachments.find(
                      a => a.tool && String(a.tool) === String(tool._id),
                    );
                    const key = `tool:${String(tool._id)}`;
                    const isOpen = usageOpen.has(key);
                    const hasUsage = Boolean((att?.usage || '').trim());
                    return (
                      <Box key={tool._id} sx={styles.toolCard}>
                        <Box
                          sx={styles.toolCardHeader}
                          onClick={() => {
                            if (agentEdit?.flow?._id && agentEdit?.authCompany) {
                              navigate(
                                buildAgenticPath(agentEdit.authCompany, {
                                  flowId: agentEdit.flow._id,
                                  section: 'tools',
                                  toolId: tool._id,
                                }),
                                { state: { fromSkillId: skill._id } },
                              );
                            } else if (context?.push) {
                              context.push({ type: 'agent-tool', agentTool: tool._id });
                            }
                          }}
                        >
                          <Avatar variant="rounded" sx={styles.toolIcon}>
                            <WrenchOutlineIcon size={18} />
                          </Avatar>
                          <Box sx={styles.toolText}>
                            <Box sx={styles.toolNameRow}>
                              <Typography sx={styles.toolName}>{tool.name}</Typography>
                              <Chip
                                size="small"
                                sx={styles.kindBadge}
                                label={(tool.kind || 'code').toUpperCase()}
                              />
                            </Box>
                            {tool.description && (
                              <Typography sx={styles.toolDesc}>{tool.description}</Typography>
                            )}
                            {!isOpen && (
                              <Button
                                size="small"
                                variant="text"
                                color="inherit"
                                sx={styles.toolCardUsageLink}
                                onClick={e => {
                                  e.stopPropagation();
                                  toggleUsageOpen(key, true);
                                }}
                              >
                                {hasUsage
                                  ? I18n.t('AgenticChatFlow.EditUsage')
                                  : I18n.t('AgenticChatFlow.AddUsage')}
                              </Button>
                            )}
                          </Box>
                          <ChevronRightIcon size={20} />
                        </Box>
                        {isOpen && (
                          <AttachedToolUsageEditor
                            styles={styles}
                            value={att?.usage || ''}
                            autoFocus={!hasUsage}
                            onSave={usage => saveAttachmentUsage({ tool: tool._id }, usage)}
                            onCollapse={() => toggleUsageOpen(key, false)}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {tab === 'knowledge' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={styles.sectionHeader}>
                <Box>
                  <Typography sx={styles.sectionTitle}>
                    {I18n.t('AgenticChatFlow.Knowledge')}
                  </Typography>
                  <Typography sx={styles.sectionHelper}>
                    {I18n.t('AgenticChatFlow.KnowledgeTabHelper')}
                  </Typography>
                </Box>
                <Stack direction="row" sx={styles.sectionActions}>
                  <Button
                    sx={styles.secondaryButton}
                    startIcon={<CogOutlineIcon size={16} />}
                    onClick={openManageKnowledge}
                  >
                    {I18n.t('AgenticChatFlow.ManageKnowledge')}
                  </Button>
                  {agentEdit?.onCreateKnowledge && (
                    <Button sx={styles.secondaryButton} onClick={agentEdit.onCreateKnowledge}>
                      {I18n.t('AgenticChatFlow.CreateKnowledge')}
                    </Button>
                  )}
                </Stack>
              </Box>
              {totalKnowledgeAttached === 0 ? (
                <Box sx={styles.emptyCard}>{I18n.t('AgenticChatFlow.NoKnowledgeForSkill')}</Box>
              ) : (
                <Box sx={styles.toolList}>
                  {attachedKnowledge.map(k => {
                    const att = skillKnowledgeAttachments.find(
                      a => a.knowledge && String(a.knowledge) === String(k._id),
                    );
                    const key = `knowledge:${String(k._id)}`;
                    const isOpen = usageOpen.has(key);
                    const hasUsage = Boolean((att?.usage || '').trim());
                    return (
                      <Box key={k._id} sx={styles.toolCard}>
                        <Box
                          sx={styles.toolCardHeader}
                          onClick={() => {
                            if (agentEdit?.flow?._id && agentEdit?.authCompany) {
                              navigate(
                                buildAgenticPath(agentEdit.authCompany, {
                                  flowId: agentEdit.flow._id,
                                  section: 'knowledge',
                                  knowledgeId: k._id,
                                }),
                                { state: { fromSkillId: skill._id } },
                              );
                            }
                          }}
                        >
                          <Avatar variant="rounded" sx={styles.toolIcon}>
                            <DatabaseOutlineIcon size={18} />
                          </Avatar>
                          <Box sx={styles.toolText}>
                            <Box sx={styles.toolNameRow}>
                              <Typography sx={styles.toolName}>
                                {k.displayName || k.name}
                              </Typography>
                            </Box>
                            {k.description && (
                              <Typography sx={styles.toolDesc}>{k.description}</Typography>
                            )}
                            {!isOpen && (
                              <Button
                                size="small"
                                variant="text"
                                color="inherit"
                                sx={styles.toolCardUsageLink}
                                onClick={e => {
                                  e.stopPropagation();
                                  toggleUsageOpen(key, true);
                                }}
                              >
                                {hasUsage
                                  ? I18n.t('AgenticChatFlow.EditUsage')
                                  : I18n.t('AgenticChatFlow.AddUsage')}
                              </Button>
                            )}
                          </Box>
                          <ChevronRightIcon size={20} />
                        </Box>
                        {isOpen && (
                          <AttachedToolUsageEditor
                            styles={styles}
                            value={att?.usage || ''}
                            autoFocus={!hasUsage}
                            onSave={usage => saveKnowledgeAttachmentUsage(k._id, usage)}
                            onCollapse={() => toggleUsageOpen(key, false)}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {tab === 'instructions' && (
        <InstructionSaveFooter
          dirty={instructionDirty}
          saveFeedback={instructionSaveFeedback}
          onSave={handleInstructionFooterSave}
        />
      )}

      <AttachToolsDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        tools={tools}
        selectedAttachments={skillAttachments}
        onSave={attachments => saveAttachments(attachments)}
        onCreateTool={() => startCreateTool('http')}
      />

      <AttachKnowledgeDialog
        open={manageKnowledgeOpen}
        onClose={() => setManageKnowledgeOpen(false)}
        knowledge={catalogKnowledge}
        selectedAttachments={skillKnowledgeAttachments}
        onSave={attachments => saveKnowledgeAttachments(attachments)}
        onCreateKnowledge={agentEdit?.onCreateKnowledge}
        dialogTitle={I18n.t('AgenticChatFlow.ManageKnowledge')}
      />

      <Menu
        anchorEl={newToolMenuAnchor}
        open={Boolean(newToolMenuAnchor)}
        onClose={() => setNewToolMenuAnchor(null)}
      >
        <MenuItem onClick={() => startCreateTool('http')}>
          {I18n.t('AgenticChatFlow.CreateTool')} (HTTP)
        </MenuItem>
        <MenuItem onClick={() => startCreateTool('code')}>
          {I18n.t('AgenticChatFlow.CreateTool')} (code)
        </MenuItem>
      </Menu>

      <Dialog
        open={createToolOpen}
        onClose={() => !creatingTool && setCreateToolOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>{I18n.t('AgenticChatFlow.CreateTool')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label={I18n.t('AgenticChatFlow.Name')}
              value={newToolForm.name}
              onChange={e => setNewToolForm({ ...newToolForm, name: e.target.value })}
              size="small"
              fullWidth
              autoFocus
            />
            {createToolError ? (
              <Typography color="error" variant="body2">
                {createToolError}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateToolOpen(false)} disabled={creatingTool} color="inherit">
            {I18n.t('AgenticChatFlow.Cancel')}
          </Button>
          <Button
            onClick={handleCreateTool}
            variant="contained"
            color="primary"
            disableElevation
            disabled={creatingTool || !newToolForm.name.trim()}
          >
            {I18n.t('AgenticChatFlow.Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

SkillEditor.propTypes = {
  skillId: string,
  authCompany: object,
  embedded: bool,
};

export default connector(SkillEditor);
